import { useState, useMemo } from 'react';
import Decimal from 'decimal.js';
import moment from "moment-timezone";
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Combobox } from '@/components/ui/combobox';
import { useToast } from '@/components/ui/use-toast';
import { useListCustomers } from '@/lib/api/customer';
import { useUpsertOrderMetadata, TtOrderMetadata, TtOrderMetadataCustomerSplit } from '@/lib/api/risk/brokerage/tt';
import { SelectOptionItem } from '@/lib/models/ui';
import { Badge } from '@/components/ui/badge';
import { cn, CommonDestinationTimezone } from '@/lib/utils';
import { guessOrderTypeFromText } from '../transform';
import { ContractType, ContractTypeOptions } from '@/lib/api/otc';

type OrderType = 'hta' | 'pricing' | 'position_adjust' | 'cash' | 'otc' | 'none';
type OtcMode = 'hedgeable' | 'reown';

type ValueSource = 'current' | 'suggested' | 'updated';

interface SuggestedValues {
  customerId?: string;
  orderType?: OrderType;
}

interface OrderTextFields {
  textA?: string | null;
  textB?: string | null;
  textC?: string | null;
  textTT?: string | null;
}

interface CustomerOption {
  id: string;
  longName: string;
}

interface CustomerSplitEntry {
  customer_id: string;
  /** Quantity as a string (user-facing). Stored as percentage internally on submit. */
  quantity: string;
}

interface AttachCustomerFormProps {
  guidOrderId: string;
  existingMetadata?: TtOrderMetadata | null;
  onSuccess?: () => void;
  isInline?: boolean;
  isModal?: boolean;
  orderTextFields?: OrderTextFields;
  instrumentName?: string | null;
  totalQuantity?: Decimal | null;
  orderQuantity?: Decimal | null;
  remainingQuantity?: Decimal | null;
  filledQuantity?: Decimal | null;
}

const REOWN_CONTRACT_TYPES = new Set<string>([
  ContractType.BasePriceReown,
  ContractType.RangePriceReown,
  ContractType.ReOwnWDU,
]);

const hedgeableContractTypeOptions = ContractTypeOptions.filter(
  (opt) => !REOWN_CONTRACT_TYPES.has(opt.value)
);

const reownContractTypeOptions = ContractTypeOptions.filter(
  (opt) => REOWN_CONTRACT_TYPES.has(opt.value)
);

// ---------------------------------------------------------------------------
// Customer suggestion scoring
// ---------------------------------------------------------------------------

const SUGGESTION_CONFIDENCE_THRESHOLD = 0.65;

/** Tokens that are too generic to be meaningful for matching. */
const NOISE_TOKENS = new Set([
  'farm', 'farms', 'new','hta', 'spot','otc','pricing', 'pxing', 'pos', 'adj', 'llc', 'ltd', 'group',
  'inc', 'co', 'corp', 'the', 'and', 'of', 'grain', 'grains', 'ag', 'agr', 'or', '&', 'error'
]);

/** Normalise a raw string into matchable segments (≥ 3 chars, not noise). */
function toSegments(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .map(t => t.trim())
    .filter(t => t.length >= 3 && !NOISE_TOKENS.has(t));
}

/** Levenshtein edit distance between two strings. */
function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  // Use a single row rolling approach
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const curr: number[] = [i];
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1,          // insertion
        prev[j] + 1,               // deletion
        prev[j - 1] + cost         // substitution
      );
    }
    prev = curr;
  }
  return prev[n];
}

/**
 * Score how well a single input segment matches a single customer name segment.
 *
 * Tiers (returned value):
 *   1.0  — exact match
 *   0.95 — differs only by a trailing 's' (plural/singular)
 *   0–<0.95 — normalised Levenshtein similarity
 */
function segmentScore(input: string, nameSegment: string): number {
  if (input === nameSegment) return 1.0;

  // Plural / singular — one is the other with a trailing 's'
  const shorter = input.length < nameSegment.length ? input : nameSegment;
  const longer  = input.length < nameSegment.length ? nameSegment : input;
  if (longer === shorter + 's') return 0.95;

  // Levenshtein similarity
  const maxLen = Math.max(input.length, nameSegment.length);
  if (maxLen === 0) return 1.0;
  const dist = levenshtein(input, nameSegment);
  return 1 - dist / maxLen;
}

/**
 * Score an input segment against all segments of a customer name.
 * Returns the best per-segment score.
 */
function bestSegmentScore(inputSeg: string, nameSegments: string[]): number {
  if (nameSegments.length === 0) return 0;
  return nameSegments.reduce((best, ns) => Math.max(best, segmentScore(inputSeg, ns)), 0);
}

/**
 * Overall confidence score for a customer given the input text segments.
 *
 * Algorithm:
 *  - For each *customer name segment*, find the best matching input segment score.
 *  - Average those scores — this rewards customers whose names are well-covered
 *    by the input text.
 *  - Input segments that do not match any name segment don't hurt the score
 *    (they are irrelevant noise from the order text).
 */
function customerConfidenceScore(
  inputSegments: string[],
  nameSegments: string[]
): number {
  if (nameSegments.length === 0 || inputSegments.length === 0) return 0;

  const nameScores = nameSegments.map(ns => bestSegmentScore(ns, inputSegments));
  const avg = nameScores.reduce((sum, s) => sum + s, 0) / nameScores.length;
  return avg;
}

/**
 * Check whether a raw (lowercased, whitespace-collapsed) field string contains
 * an abbreviated reference to a customer name.
 *
 * Matches patterns like:
 *   customer  "Walter Matthew"  →  input contains "walter m" or "walterm"
 *   customer  "Matthew Walter"  →  input contains "matthew w" or "mattheww"
 *
 * For every pair of distinct name parts (a, b) we check whether the field
 * contains either:
 *   - "<a> <b[0]>"  (spaced:   "walter m")
 *   - "<a><b[0]>"   (unspaced: "walterm")
 */
function fieldMatchesAbbreviation(fieldRaw: string, customerNameRaw: string): boolean {
  const nameParts = customerNameRaw
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .map(p => p.trim())
    .filter(p => p.length > 0);

  if (nameParts.length < 2) return false;

  const haystack = fieldRaw.toLowerCase();

  for (let i = 0; i < nameParts.length; i++) {
    for (let j = 0; j < nameParts.length; j++) {
      if (i === j) continue;
      const full = nameParts[i];
      const initial = nameParts[j][0];
      if (!initial) continue;
      // "walter m" or "walterm"
      if (haystack.includes(`${full} ${initial}`) || haystack.includes(`${full}${initial}`)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Returns true if any of the raw text fields contain an abbreviated reference
 * to the customer's name.
 */
function anyFieldMatchesAbbreviation(
  fields: Array<string | null | undefined>,
  customerLongName: string
): boolean {
  return fields.some(f => f && fieldMatchesAbbreviation(f, customerLongName));
}

/**
 * Words stripped from both sides before collapsed matching.
 * Kept intentionally small — only words that are truly meaningless suffixes
 * that would prevent a good collapsed match (e.g. "A & A Farms LLC" vs "A & AFarms").
 */
const COLLAPSED_STRIP_WORDS = /\b(farm|llc|ltd|inc|corp|co|new|hta|otc|error|group)\b/g;

/**
 * Normalise a string for whitespace-collapsed matching:
 * lowercase, strip a small set of legal suffixes, strip non-alphanumeric, remove all whitespace.
 */
function normaliseCollapsed(text: string): string {
  return text
    .toLowerCase()
    .replace(COLLAPSED_STRIP_WORDS, '')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Check whether a field contains a customer name accounting for bad whitespace
 * splits (e.g. "A & AFarms" should match "A & A Farms").
 *
 * Both sides are fully collapsed (no spaces, no punctuation, minimal suffix strip)
 * before substring matching, so run-together tokens and punctuation variations
 * are handled without affecting the fuzzy scoring path.
 */
function fieldMatchesCollapsed(fieldRaw: string, customerNameRaw: string): boolean {
  const fieldCollapsed = normaliseCollapsed(fieldRaw);
  const nameCollapsed  = normaliseCollapsed(customerNameRaw);
  if (nameCollapsed.length === 0) return false;
  if (fieldCollapsed.length === 0) return false;

  return nameCollapsed.startsWith(fieldCollapsed) || nameCollapsed.endsWith(fieldCollapsed);
}

/**
 * Returns true if any field matches the customer name after full whitespace collapse.
 */
function anyFieldMatchesCollapsed(
  fields: Array<string | null | undefined>,
  customerLongName: string
): boolean {
  return fields.some(f => f && fieldMatchesCollapsed(f, customerLongName));
}

/**
 * Suggests a customer ID and order type from order text fields.
 * Each text field is scored independently against each customer name — the
 * customer's best score across all fields wins. This prevents noise from
 * unrelated fields polluting the match for the field that actually contains
 * the customer name.
 * Only suggests a customer when confidence is ≥ SUGGESTION_CONFIDENCE_THRESHOLD.
 * Ties are broken by customer id (lexicographic) for deterministic results.
 */
function getSuggestedValues(
  orderTextFields: OrderTextFields | undefined,
  customers: CustomerOption[]
): SuggestedValues {
  const result: SuggestedValues = {};

  if (orderTextFields) {
    const guessed = guessOrderTypeFromText(
      orderTextFields.textA ?? null,
      orderTextFields.textB ?? null,
      orderTextFields.textC ?? null,
      orderTextFields.textTT ?? null
    );
    if (guessed) {
      result.orderType = guessed;
    }
  }

  if (!orderTextFields || customers.length === 0) {
    return result;
  }

  const { textA, textB, textC, textTT } = orderTextFields;

  // Score each field independently so a noisy field can't drag down a clean match
  const fieldSegments = [textA, textB, textC, textTT]
    .map(t => (t ? toSegments(t) : []))
    .filter(segs => segs.length > 0);

  if (fieldSegments.length === 0) {
    return result;
  }

  const rawFields = [textA, textB, textC, textTT];

  // First: collapsed whitespace match — runs before fuzzy scoring so a tight
  // structural match (e.g. "A & AFarms" → "A & A Farms") wins outright and
  // doesn't get beaten by something the scorer happens to find "closer".
  // Sort by collapsed name length descending so longer/more-specific names
  // (e.g. "Person A or Person B") beat shorter prefixes (e.g. "Person A").
  // Takes the longest match only if it is strictly longer than the next candidate,
  // otherwise falls through (ambiguous).
  const collapsedMatches = customers
    .filter(customer => anyFieldMatchesCollapsed(rawFields, customer.longName))
    .sort((a, b) => {
      const lenDiff = normaliseCollapsed(b.longName).length - normaliseCollapsed(a.longName).length;
      if (lenDiff !== 0) return lenDiff;
      return a.id.localeCompare(b.id);
    });

  if (
    collapsedMatches.length >= 1 &&
    (
      collapsedMatches.length === 1 ||
      normaliseCollapsed(collapsedMatches[0].longName).length >
      normaliseCollapsed(collapsedMatches[1].longName).length
    )
  ) {
    result.customerId = collapsedMatches[0].id;
    return result;
  }

  const scored = customers
    .map(customer => {
      const nameSegments = toSegments(customer.longName);
      if (nameSegments.length === 0) return { customer, score: 0 };
      // Best score across all fields — the customer name will be in at most one field
      const score = fieldSegments.reduce(
        (best, segs) => Math.max(best, customerConfidenceScore(segs, nameSegments)),
        0
      );
      return { customer, score };
    })
    .filter(entry => entry.score >= SUGGESTION_CONFIDENCE_THRESHOLD)
    // Deterministic sort: score descending, then id ascending for tie-breaking
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.customer.id.localeCompare(b.customer.id);
    });

  if (scored.length > 0) {
    result.customerId = scored[0].customer.id;
    return result;
  }

  // Fallback: abbreviation matching (e.g. "Walter M" or "WalterM" → "Walter Matthew")
  // Checked on raw field text before any stripping, so noise filtering doesn't affect it.
  // Only fires when exactly one customer matches — multiple matches means ambiguous.
  const abbreviationMatches = customers
    .filter(customer => anyFieldMatchesAbbreviation(rawFields, customer.longName))
    .sort((a, b) => a.id.localeCompare(b.id));

  if (abbreviationMatches.length === 1) {
    result.customerId = abbreviationMatches[0].id;
  }

  return result;
}

function ValueSourceBadge({ source }: { source: ValueSource }) {
  const config = {
    current: { label: 'Current', className: 'bg-muted text-muted-foreground' },
    suggested: { label: 'Suggested', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
    updated: { label: 'Updated', className: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
  };
  const { label, className } = config[source];
  return <Badge variant="outline" className={cn('ml-2 text-xs font-normal', className)}>{label}</Badge>;
}

function getOrderTypeLabel(orderType: OrderType): string {
  const labels: Record<OrderType, string> = {
    none: 'None',
    hta: 'HTA',
    pricing: 'Pricing',
    position_adjust: 'Position Adjust',
    cash: 'Cash',
    otc: 'OTC',
  };
  return labels[orderType];
}

/** Convert a split_percentage (0–100) to a quantity given a totalQuantity. */
function percentageToQuantity(pct: number | string, total: Decimal): Decimal {
  return new Decimal(pct).div(100).mul(total).toDecimalPlaces(0);
}

export function AttachCustomerForm({
  guidOrderId,
  existingMetadata,
  onSuccess,
  isInline = false,
  isModal = false,
  orderTextFields,
  instrumentName,
  totalQuantity,
  orderQuantity,
  remainingQuantity,
  filledQuantity,
}: AttachCustomerFormProps) {
  const { toast } = useToast();
  const customersResult = useListCustomers({ minimal: true });
  const upsertMetadata = useUpsertOrderMetadata();

  const [nonClient, setNonClient] = useState(false);

  const customerOptions: SelectOptionItem<string>[] = useMemo(() => {
    const customers = customersResult.data?.data ?? [];
    return customers
      .filter((customer: { id: string; hasRiskProfile?: boolean }) => {
        if (nonClient) return !customer.hasRiskProfile;
        return !!customer.hasRiskProfile;
      })
      .map((customer: { id: string; short_name?: string; long_name?: string }) => ({
        value: customer.id,
        label: customer.long_name || customer.short_name,
      }));
  }, [customersResult.data?.data, nonClient]);

  const customerOptionsForMatching: CustomerOption[] = useMemo(() => {
    return customerOptions.map(opt => ({
      id: opt.value,
      longName: opt.label ?? '',
    }));
  }, [customerOptions]);

  const suggestedValues = useMemo(
    () => getSuggestedValues(orderTextFields, customerOptionsForMatching),
    [orderTextFields, customerOptionsForMatching]
  );;

  const getExistingOrderType = (): OrderType => {
    if (!existingMetadata) return 'none';
    if (existingMetadata.expected_hta) return 'hta';
    if (existingMetadata.expected_pricing) return 'pricing';
    if (existingMetadata.expected_position_adjust) return 'position_adjust';
    if (existingMetadata.expected_cash) return 'cash';
    if (existingMetadata.expected_otc) return 'otc';
    return 'none';
  };

  const existingOrderType = getExistingOrderType();

  const getInitialCustomerSplits = (): CustomerSplitEntry[] => {
    if (existingMetadata?.SpecifiedCustomers && existingMetadata.SpecifiedCustomers.length > 0) {
      return existingMetadata.SpecifiedCustomers.map((c: TtOrderMetadataCustomerSplit) => ({
        customer_id: c.customer_id,
        quantity: totalQuantity != null
          ? percentageToQuantity(c.split_percentage, totalQuantity).toFixed(0)
          : String(c.split_percentage),
      }));
    }
    if (suggestedValues.customerId) {
      return [{ customer_id: suggestedValues.customerId, quantity: totalQuantity != null ? totalQuantity.toFixed(0) : '' }];
    }
    return [{ customer_id: '', quantity: totalQuantity != null ? totalQuantity.toFixed(0) : '' }];
  };

  const getInitialOrderType = (): OrderType => {
    if (existingOrderType !== 'none') return existingOrderType;
    if (suggestedValues.orderType) return suggestedValues.orderType;
    return 'none';
  };

  const getInitialOtcMode = (): OtcMode | null => {
    const existing = existingMetadata?.otc_contract_type;
    if (!existing) return null;
    return REOWN_CONTRACT_TYPES.has(existing) ? 'reown' : 'hedgeable';
  };

  const currentYear = moment.tz(CommonDestinationTimezone).format("YYYY");

  const [customerSplits, setCustomerSplits] = useState<CustomerSplitEntry[]>(getInitialCustomerSplits());
  const [firstRowUserEdited, setFirstRowUserEdited] = useState(false);
  const [selectedCropYear, setSelectedCropYear] = useState<string>(
    existingMetadata?.crop_year !== 0 ? existingMetadata?.crop_year?.toString() ?? currentYear : currentYear
  );
  const [orderType, setOrderType] = useState<OrderType>(getInitialOrderType());
  const [shouldIgnore, setShouldIgnore] = useState<boolean>(existingMetadata?.should_ignore_order ?? false);
  const [otcMode, setOtcMode] = useState<OtcMode | null>(getInitialOtcMode());
  const [otcContractType, setOtcContractType] = useState<string>(existingMetadata?.otc_contract_type ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getCustomerName = (customerId: string): string => {
    const option = customerOptions.find(opt => opt.value === customerId);
    return option?.label ?? "";
  };

  const getCustomerValueSource = (): ValueSource => {
    const firstId = customerSplits[0]?.customer_id ?? '';
    const existingFirstId = existingMetadata?.SpecifiedCustomers?.[0]?.customer_id ?? '';
    if (existingFirstId && firstId !== existingFirstId) return 'updated';
    if (existingFirstId && firstId === existingFirstId) return 'current';
    if (!existingFirstId && suggestedValues.customerId && firstId === suggestedValues.customerId) return 'suggested';
    if (!existingFirstId && firstId) return 'updated';
    return 'current';
  };

  const getOrderTypeValueSource = (): ValueSource => {
    if (existingOrderType !== 'none') {
      if (orderType !== existingOrderType) return 'updated';
      return 'current';
    }
    if (suggestedValues.orderType && orderType === suggestedValues.orderType) return 'suggested';
    if (orderType !== 'none') return 'updated';
    return 'current';
  };

  const handleOtcModeSelect = (mode: OtcMode) => {
    setOtcMode(mode);
    setOtcContractType('');
  };

  const handleAddCustomerSplit = () => {
    setCustomerSplits(prev => [...prev, { customer_id: '', quantity: '' }]);
  };

  const handleRemoveCustomerSplit = (index: number) => {
    setCustomerSplits(prev => {
      const next = prev.filter((_, i) => i !== index);
      // Recalculate row 0 from remaining splits if it hasn't been manually edited
      if (!firstRowUserEdited && totalQuantity != null && next.length > 1) {
        const otherSum = next.slice(1).reduce((sum, entry) => {
          try { return entry.quantity ? sum.plus(new Decimal(entry.quantity)) : sum; } catch { return sum; }
        }, new Decimal(0));
        const remainder = Decimal.max(0, totalQuantity.minus(otherSum));
        next[0] = { ...next[0], quantity: remainder.toFixed(0) };
      }
      return next;
    });
  };

  const handleCustomerSplitChange = (index: number, field: keyof CustomerSplitEntry, value: string) => {
    if (field === 'quantity' && index === 0) {
      setFirstRowUserEdited(true);
    }
    setCustomerSplits(prev => {
      const next = prev.map((entry, i) => i === index ? { ...entry, [field]: value } : entry);
      // When a non-first row's quantity changes and row 0 hasn't been manually edited,
      // set row 0's quantity to totalQuantity - sum(rows 1..n)
      if (field === 'quantity' && index !== 0 && !firstRowUserEdited && totalQuantity != null) {
        const otherSum = next.slice(1).reduce((sum, entry) => {
          try { return entry.quantity ? sum.plus(new Decimal(entry.quantity)) : sum; } catch { return sum; }
        }, new Decimal(0));
        const remainder = Decimal.max(0, totalQuantity.minus(otherSum));
        next[0] = { ...next[0], quantity: remainder.toFixed(0) };
      }
      return next;
    });
  };

  /** When a customer is selected and there's only one split, default quantity to totalQuantity. */
  const handleCustomerSelect = (index: number, value: string) => {
    setCustomerSplits(prev => {
      const updated = prev.map((entry, i) => i === index ? { ...entry, customer_id: value } : entry);
      // If this is the only split row and quantity is empty, default to full quantity
      if (updated.length === 1 && !updated[0].quantity && totalQuantity != null) {
        return [{ ...updated[0], quantity: totalQuantity.toFixed(0) }];
      }
      return updated;
    });
  };

  const handleOrderTypeChange = (value: string) => {
    setOrderType(value as OrderType);
    if (value !== 'otc') {
      setOtcMode(null);
      setOtcContractType('');
    }
  };

  /** Sum of all customer quantities (ignoring empty). */
  const totalAssignedQuantity = useMemo(() => {
    return customerSplits.reduce((sum, entry) => {
      try {
        return entry.quantity ? sum.plus(new Decimal(entry.quantity)) : sum;
      } catch {
        return sum;
      }
    }, new Decimal(0));
  }, [customerSplits]);

  /** LHC remainder = totalQuantity - totalAssigned. Only meaningful if totalQuantity is known. */
  const lhcQuantity = totalQuantity != null
    ? Decimal.max(0, totalQuantity.minus(totalAssignedQuantity))
    : null;

  const hasValidCustomer = useMemo(() => {
    const hasNamedCustomer = customerSplits.some(s => s.customer_id && s.quantity);
    const lhcCoversAll = lhcQuantity != null && lhcQuantity.gt(0);
    return hasNamedCustomer || lhcCoversAll;
  }, [customerSplits, lhcQuantity]);

  const isFormValid =
    hasValidCustomer &&
    !!selectedCropYear &&
    orderType !== 'none' &&
    (orderType !== 'otc' || !!otcContractType);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const firstCustomerId = customerSplits[0]?.customer_id ?? '';
    if (!firstCustomerId) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one customer',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedCropYear) {
      toast({
        title: 'Validation Error',
        description: 'Please select a crop year',
        variant: 'destructive',
      });
      return;
    }

    if (!orderType || orderType === 'none') {
      toast({
        title: 'Validation Error',
        description: 'Please select a contract type',
        variant: 'destructive',
      });
      return;
    }

    if (orderType === 'otc' && !otcContractType) {
      toast({
        title: 'Validation Error',
        description: 'Please select an OTC contract type',
        variant: 'destructive',
      });
      return;
    }

    const validSplits = customerSplits.filter(s => s.customer_id);
    if (validSplits.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one customer',
        variant: 'destructive',
      });
      return;
    }

    // Validate quantities don't exceed total
    if (totalQuantity != null && totalAssignedQuantity.gt(totalQuantity)) {
      toast({
        title: 'Validation Error',
        description: `Customer quantities (${totalAssignedQuantity.toFixed(0)}) exceed total order quantity (${totalQuantity.toFixed(0)})`,
        variant: 'destructive',
      });
      return;
    }

    // Convert quantities to percentages for the API
    const splitsWithPercentages = validSplits.map(s => {
      let pct: Decimal;
      if (totalQuantity != null && totalQuantity.gt(0)) {
        try {
          pct = new Decimal(s.quantity || 0).div(totalQuantity).mul(100);
        } catch {
          pct = new Decimal(0);
        }
      } else {
        // No totalQuantity — treat the stored value as a percentage directly
        try {
          pct = new Decimal(s.quantity || 0);
        } catch {
          pct = new Decimal(0);
        }
      }
      return {
        customer_id: s.customer_id,
        split_percentage: pct.toString(),
      };
    });

    // Compute LHC percentage from lhcQuantity
    let lhcSplitPercentage: string | null = null;
    if (totalQuantity != null && totalQuantity.gt(0) && lhcQuantity != null) {
      lhcSplitPercentage = lhcQuantity.div(totalQuantity).mul(100).toString();
    }

    setIsSubmitting(true);
    try {
      const result = await upsertMetadata.mutateAsync({
        guid_order_id: guidOrderId,
        expected_hta: orderType === 'hta',
        expected_pricing: orderType === 'pricing',
        expected_position_adjust: orderType === 'position_adjust',
        expected_cash: orderType === 'cash',
        expected_otc: orderType === 'otc',
        otc_contract_type: orderType === 'otc' ? otcContractType : undefined,
        should_ignore_order: shouldIgnore,
        crop_year: parseInt(selectedCropYear),
        lhc_split_percentage: lhcSplitPercentage,
        customers: splitsWithPercentages,
      });

      if (result.success) {
        toast({
          title: 'Success',
          description: 'Order metadata updated successfully',
        });
        onSuccess?.();
      } else {
        throw new Error(result.message || 'Failed to update metadata');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update metadata';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasExistingMetadata = !!existingMetadata;

  const hasTextFields = (orderTextFields && (
    orderTextFields.textA || orderTextFields.textB || orderTextFields.textC || orderTextFields.textTT
  )) || !!instrumentName || orderQuantity != null || remainingQuantity != null || filledQuantity != null;

  const availableOtcContractTypes = otcMode === 'reown'
    ? reownContractTypeOptions
    : otcMode === 'hedgeable'
      ? hedgeableContractTypeOptions
      : [];

  const cropYearOptions: SelectOptionItem<string>[] = Array.from({ length: 10 }, (_, i) => {
    const year = parseInt(currentYear) - i + 3;
    return {
      value: year.toString(),
      label: year.toString(),
    };
  });

  return (
    <form onSubmit={handleSubmit} className={isInline ? 'space-y-4' : 'space-y-4 py-4'}>
      {/* Order Text Fields Summary - only shown in modal */}
      {isModal && hasTextFields && (
        <div className="p-3 bg-muted/50 rounded-lg border text-sm">
          <div className="grid grid-cols-4 gap-x-4 gap-y-1">
            <span className="grid grid-cols-[auto,auto,auto,auto] col-span-4  gap-4">
            {orderTextFields?.textA && (
              <span className="grid">
                <span className="text-muted-foreground">Text A:</span>
                <span className="font-medium">{orderTextFields.textA}</span>
              </span>
            )}
            {orderTextFields?.textB && (
              <span className="grid">
                <span className="text-muted-foreground">Text B:</span>
                <span className="font-medium">{orderTextFields.textB}</span>
              </span>
            )}
            {orderTextFields?.textC && (
              <span className="grid">
                <span className="text-muted-foreground">Text C:</span>
                <span className="font-medium">{orderTextFields.textC}</span>
              </span>
            )}
            {orderTextFields?.textTT && (
              <span className="grid">
                <span className="text-muted-foreground">Text TT:</span>
                <span className="font-medium">{orderTextFields.textTT}</span>
              </span>
            )}
            </span>
            <span className="col-span-4 grid grid-cols-[1fr,auto,auto,auto] gap-4">
            {instrumentName && (
              <span className="grid">
                <span className="text-muted-foreground">Instrument:</span>
                <span className="font-medium">{instrumentName}</span>
              </span>
            )}
            {orderQuantity != null && (
              <span className="grid">
                <span className="text-muted-foreground">Order Qty:</span>
                <span className="font-medium">{orderQuantity.toFixed(0)}</span>
              </span>
            )}
            {remainingQuantity != null && (
              <span className="grid">
                <span className="text-muted-foreground">Remaining Qty:</span>
                <span className="font-medium">{remainingQuantity.toFixed(0)}</span>
              </span>
            )}
            {filledQuantity != null && (
              <span className="grid">
                <span className="text-muted-foreground">Filled Qty:</span>
                <span className="font-medium">{filledQuantity.toFixed(0)}</span>
              </span>
            )}
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column: Existing State (Read-only) */}
        <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
            Existing Metadata
          </h3>
          {hasExistingMetadata ? (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Customer(s)</Label>
                {existingMetadata.SpecifiedCustomers && existingMetadata.SpecifiedCustomers.length > 0 ? (
                  <div className="space-y-1">
                    {existingMetadata.SpecifiedCustomers.map((c: TtOrderMetadataCustomerSplit, i: number) => (
                      <p key={i} className="text-sm font-medium">
                        {c.Customer?.long_name ?? getCustomerName(c.customer_id)}
                        {existingMetadata.SpecifiedCustomers.length > 1 && (
                          <span className="text-muted-foreground ml-1 text-xs">({c.split_percentage}%)</span>
                        )}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm font-medium">
                    <span className="text-muted-foreground italic">Not set</span>
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Order Type</Label>
                <p className="text-sm font-medium">{getOrderTypeLabel(existingOrderType)}</p>
              </div>
              {existingMetadata.expected_otc && existingMetadata.otc_contract_type && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">OTC Contract Type</Label>
                  <p className="text-sm font-medium">
                    {ContractTypeOptions.find(o => o.value === existingMetadata.otc_contract_type)?.label ?? existingMetadata.otc_contract_type}
                  </p>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Crop Year</Label>
                <p className="text-sm font-medium">{existingMetadata?.crop_year ?? ""}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Should Ignore</Label>
                <p className="text-sm font-medium">{existingMetadata.should_ignore_order ? 'Yes' : 'No'}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-24 text-muted-foreground italic text-sm">
              No existing metadata
            </div>
          )}
        </div>

        {/* Right Column: Editable Form */}
        <div className="space-y-4">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
            {hasExistingMetadata ? 'Update Metadata' : 'Set Metadata'}
          </h3>

          {/* Customer Splits */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Label>Customer(s) <span className="text-destructive">*</span></Label>
              {customerSplits[0]?.customer_id && <ValueSourceBadge source={getCustomerValueSource()} />}
              {/* Should Ignore */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is-error"
                  checked={shouldIgnore}
                  onCheckedChange={(checked) => setShouldIgnore(checked === true)}
                />
                <Label htmlFor="is-error" className="font-bold cursor-pointer">
                  Is Error
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              {customerSplits.map((entry, index) => (
                <div key={index} className="flex gap-2 items-start">
                  {/* Quantity input first */}
                  <div className="w-28 shrink-0">
                    <input
                      type="number"
                      min="0"
                      max={totalQuantity?.toFixed(0) ?? undefined}
                      step="1"
                      value={entry.quantity}
                      onChange={(e) => handleCustomerSplitChange(index, 'quantity', e.target.value)}
                      className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                      placeholder="Qty"
                    />
                  </div>
                  {/* Customer combobox */}
                  <div className="flex-1">
                    <Combobox
                      dwimOnChange
                      isModal={isModal}
                      placeholder="Select customer..."
                      options={customerOptions}
                      value={entry.customer_id}
                      onValueChange={(val) => handleCustomerSelect(index, val)}
                      disabled={customersResult.isLoading}
                      virtualizeList
                      popoverClassName="w-[500px] h-[200px]"
                    />
                  </div>
                  {customerSplits.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveCustomerSplit(index)}
                      className="h-9 px-2 text-destructive hover:text-destructive"
                    >
                      ✕
                    </Button>
                  )}
                </div>
              ))}

              {/* LHC remainder row */}
              {totalQuantity != null && (
                <div className="flex gap-2 items-start">
                  <div className="w-28 shrink-0">
                    <input
                      type="text"
                      value={lhcQuantity?.toFixed(0) ?? '0'}
                      readOnly
                      disabled
                      className={cn(
                        "w-full h-9 rounded-md border border-input px-3 py-1 text-sm shadow-sm",
                        "bg-muted text-muted-foreground cursor-not-allowed"
                      )}
                    />
                  </div>
                  <div className="flex-1 flex items-center h-9 px-3 rounded-md border border-input bg-muted text-muted-foreground text-sm">
                    LHC
                  </div>
                </div>
              )}

              {/* Sum line */}
              {totalQuantity != null && customerSplits.length > 1 && (
                <div className="flex gap-2 items-center pt-1 border-t border-border">
                  <div className="w-28 shrink-0 text-right pr-3 text-sm font-medium tabular-nums">
                    {totalQuantity.toFixed(0)}
                  </div>
                  <div className="flex-1 text-sm text-muted-foreground">
                    Total
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-row gap-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddCustomerSplit}
              className="mt-1"
            >
              + Add Split
            </Button>
              {/* Non Client */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="non-client"
                  checked={nonClient}
                  onCheckedChange={(checked) => setNonClient(checked === true)}
                />
                <Label htmlFor="non-client" className="font-bold cursor-pointer">
                  Non Client
                </Label>
              </div>
          </div>
          </div>

          {/* Crop Year */}
          <div className="space-y-2 grid">
            <div className="flex items-center">
              <Label>Crop Year <span className="text-destructive">*</span></Label>
            </div>
            <Combobox
              dwimOnChange
              isModal={isModal}
              placeholder="Select crop year..."
              options={cropYearOptions}
              value={selectedCropYear}
              onValueChange={setSelectedCropYear}
            />
          </div>

          {/* Order Type */}
          <div className="space-y-2">
            <div className="flex items-center">
              <Label>Order Type <span className="text-destructive">*</span></Label>
              {(orderType !== 'none' || existingOrderType !== 'none') && (
                <ValueSourceBadge source={getOrderTypeValueSource()} />
              )}
            </div>
            <RadioGroup
              required
              value={orderType}
              onValueChange={handleOrderTypeChange}
              className="flex flex-col space-y-1"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="none" id="type-none" />
                <Label htmlFor="type-none" className="font-normal cursor-pointer">None</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="hta" id="type-hta" />
                <Label htmlFor="type-hta" className="font-normal cursor-pointer">HTA</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="pricing" id="type-pricing" />
                <Label htmlFor="type-pricing" className="font-normal cursor-pointer">Pricing</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="position_adjust" id="type-position-adjust" />
                <Label htmlFor="type-position-adjust" className="font-normal cursor-pointer">Position Adjust</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="cash" id="type-cash" />
                <Label htmlFor="type-cash" className="font-normal cursor-pointer">Cash</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="otc" id="type-otc" />
                <Label htmlFor="type-otc" className="font-normal cursor-pointer">OTC</Label>
              </div>
            </RadioGroup>
          </div>

          {/* OTC Sub-section */}
          {orderType === 'otc' && (
            <div className="space-y-3 pl-4 border-l-2 border-muted">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={otcMode === 'hedgeable' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleOtcModeSelect('hedgeable')}
                >
                  Hedgeable
                </Button>
                <Button
                  type="button"
                  variant={otcMode === 'reown' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleOtcModeSelect('reown')}
                >
                  Reown
                </Button>
              </div>

              {otcMode !== null && (
                <div className="space-y-1 grid">
                  <Label>
                    OTC Contract Type <span className="text-destructive">*</span>
                  </Label>
                  <Combobox
                    dwimOnChange
                    isModal={isModal}
                    placeholder="Select contract type..."
                    options={availableOtcContractTypes}
                    value={otcContractType}
                    onValueChange={setOtcContractType}
                    popoverClassName="w-[300px]"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={isSubmitting || !isFormValid}>
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
